import React from 'react';
import { DEFAULT_DECIMAL_PRECISION } from '../../common/constants';

const DEFAULT_THOUSANDS_ABBREVIATION = 'k';
const DEFAULT_MILLIONS_ABBREVIATION = 'm';
const DEFAULT_NO_VALUE = '-';
const POSITIVE = '+';
const NEGATIVE = '-';
const LEFT_PARENTHESES = '(';
const RIGHT_PARENTHESES = ')';

export interface INumericProps {
  /** value to display */
  value?: number;
  /** show before the value */
  prefix?: string;
  /** show after the value */
  suffix?: string;
  /** what to show if number is null/undefined */
  noValue?: string;
  /** abbreviation for thousands */
  thousandsAbbreviation?: string;
  /** abbreviation for millions */
  millionsAbbreviation?: string;
  /** locale to use instead of browser default */
  locale?: string;
  /** number of decimal places to show */
  fractionDigits?: number;
  /** show up to fractionDigits number of decimal places, will add 0's at end. eg. set both to 2 for currency values */
  minimumFractionDigits?: number;
  /** abbreviate the number for thousands and millions */
  abbreviate?: boolean;
  /** show + if positive */
  showPositive?: boolean;
  /** show -ve numbers as (1.23) */
  showNegativeParentheses?: boolean;
  /** If 0 should count as no value */
  zeroIsNoValue?: boolean;
  /** What to show if value is NaN */
  nanValue?: string;
}

const getFormatter = (locale: string | undefined, options: Intl.NumberFormatOptions) => {
  try {
    return Intl.NumberFormat(locale, options);
  } catch (error) {
    console.error(error);
    return Intl.NumberFormat();
  }
};

const getAbbreviation = (value: number, abbreviate: boolean, thousandsAbbreviation: string, millionsAbbreviation: string) => {
  if (!abbreviate || value < 1000) return { val: value, abbrev: '' };
  if (value >= 1_000_000) return { val: value / 1_000_000, abbrev: millionsAbbreviation };
  return { val: value / 1000, abbrev: thousandsAbbreviation };
};

export const Numeric: React.FC<INumericProps> = ({
  value,
  prefix = '',
  suffix = '',
  noValue = DEFAULT_NO_VALUE,
  thousandsAbbreviation = DEFAULT_THOUSANDS_ABBREVIATION,
  millionsAbbreviation = DEFAULT_MILLIONS_ABBREVIATION,
  locale,
  fractionDigits = DEFAULT_DECIMAL_PRECISION,
  minimumFractionDigits = 0,
  abbreviate = false,
  showPositive = false,
  showNegativeParentheses = false,
  zeroIsNoValue = false,
  nanValue
}) => {
  if (value === null || value === undefined || (value === 0 && zeroIsNoValue)) return <>{noValue}</>;
  if (Number.isNaN(value) && nanValue) return <>{nanValue}</>;

  const options: Intl.NumberFormatOptions = { maximumFractionDigits: fractionDigits, minimumFractionDigits };
  const formatter = getFormatter(locale, options);
  const positive = showPositive && value > 0 ? POSITIVE : '';
  const negativePrefix = value < 0 ? (showNegativeParentheses ? LEFT_PARENTHESES : NEGATIVE) : '';
  const negativeSuffix = value < 0 ? (showNegativeParentheses ? RIGHT_PARENTHESES : '') : '';
  const { val, abbrev } = getAbbreviation(Math.abs(value), abbreviate, thousandsAbbreviation, millionsAbbreviation);

  return <>{`${prefix}${positive}${negativePrefix}${formatter.format(val)}${abbrev}${negativeSuffix}${suffix}`}</>;
};
